import React, { useMemo } from "react";
import { useQuery } from "urql";
import { sortBy } from "lodash";
import { Link, useParams } from "wouter";

import { mapping } from "../../../../common/src";
import { exportToXlsx } from "../../../../components/src/common/exportToXlsx";
import { ReactTable } from "../../../../components/src/react-table/ReactTable";
import { createReactTableColumnHelper, ReactTableColumnDef } from "../../../../components/src/react-table/features";

import { ACCOUNTING_RUN, ACCOUNTING_RUNS } from "../../../../graphql/src/accountingrun2/queries";
import { CURRENCY_EXPONENT } from "../../../../graphql/src/accountingrun2/currencyExponent";
import { JOURNAL_ENTRIES } from "../../../../graphql/src/journalentry2/queries";
import { DEFAULT_TACCOUNTS } from "../../../../graphql/src/taccountchart2/defaults";

import { formatDecimal } from "./formatDecimal";
import {
    AccountingCompanyTypeEnum,
    AccountingRunStatusEnum,
    CurrencyEnum,
    TAccountGroup1Enum,
    TAccountGroup2Enum
} from "../../types.generated";
import type { AccountingRun2, JournalEntry2 } from "../../types.generated";

interface StatementRow {
    kind: "group" | "account" | "sum" | "result";
    label: string;
    tAccountNumber: string;
    // amount in units of 0.0001, null for group headers
    units: bigint | null;
    level: number;
}

interface AccountBalance {
    tAccountNumber: string;
    description: string;
    // group1..group5 of the t-account, cut at the first missing or None group
    path: string[];
    units: bigint;
}

interface GroupNode {
    key: string;
    label: string;
    sorting: number;
    units: bigint;
    children: Record<string, GroupNode>;
    accounts: AccountBalance[];
}

// group with its Swedish label and sorting from the t-account group mapping, the label of a SwedishMutualFund when the company type
// has none and the group key when the group is not in the mapping
const createGroupNode = (path: string[], companyType: AccountingCompanyTypeEnum): GroupNode => {
    const mappingNode = path.reduce((node, key) => (node ? node[key] : undefined), mapping as any);
    const labels = mappingNode?.translation?.Sv || {};
    return {
        key: path[path.length - 1],
        label: labels[companyType] || labels[AccountingCompanyTypeEnum.SwedishMutualFund] || path[path.length - 1],
        sorting: typeof mappingNode?.sorting === "number" ? mappingNode.sorting : Number.MAX_SAFE_INTEGER,
        units: 0n,
        children: {},
        accounts: []
    };
};

// adds an account below root, path of root is the start of the path of the account
const addAccount = (root: GroupNode, rootDepth: number, account: AccountBalance, companyType: AccountingCompanyTypeEnum): void => {
    let node = root;
    node.units += account.units;
    for (let depth = rootDepth; depth < account.path.length; depth++) {
        const key = account.path[depth];
        if (!node.children[key]) {
            node.children[key] = createGroupNode(account.path.slice(0, depth + 1), companyType);
        }
        node = node.children[key];
        node.units += account.units;
    }
    node.accounts.push(account);
};

// header, sub groups in mapping order, accounts by number and a sum row
const addGroupRows = (node: GroupNode, level: number, rows: StatementRow[]): void => {
    rows.push({ kind: "group", label: node.label, tAccountNumber: "", units: null, level });
    for (const child of sortBy(Object.values(node.children), "sorting", "key")) {
        addGroupRows(child, level + 1, rows);
    }
    for (const account of sortBy(node.accounts, "tAccountNumber")) {
        rows.push({
            kind: "account",
            label: account.description,
            tAccountNumber: account.tAccountNumber,
            units: account.units,
            level: level + 1
        });
    }
    rows.push({
        kind: "sum",
        label: "Summa " + node.label.charAt(0).toLowerCase() + node.label.slice(1),
        tAccountNumber: "",
        units: node.units,
        level
    });
};

export function AccountingStatementsPage(): React.ReactElement {
    const { clientId, id } = useParams<{ clientId: string; id: string }>();
    const [{ fetching, error, data }] = useQuery<{ accountingRuns2: AccountingRun2[] }>({
        query: ACCOUNTING_RUN,
        variables: { id },
        requestPolicy: "network-only"
    });
    const run = data && data.accountingRuns2 && data.accountingRuns2.length > 0 ? data.accountingRuns2[0] : null;
    const [{ fetching: fetchingRuns, error: runsError, data: runsData }] = useQuery<{ accountingRuns2: AccountingRun2[] }>({
        query: ACCOUNTING_RUNS,
        variables: {
            filter: {
                clientIdIn: [clientId],
                statusIn: [AccountingRunStatusEnum.Pending, AccountingRunStatusEnum.Preliminary, AccountingRunStatusEnum.Confirmed]
            }
        },
        requestPolicy: "network-only"
    });
    // journal entries of all non deleted runs of the period
    const [{ fetching: fetchingJournalEntries, error: journalEntriesError, data: journalEntriesData }] = useQuery<{
        journalEntries2: JournalEntry2[];
    }>({
        query: JOURNAL_ENTRIES,
        variables: { clientId, accountingPeriod: run ? run.accountingPeriod : null },
        requestPolicy: "network-only",
        pause: !run
    });

    // amounts in accounting currency are shown with its ISO 4217 decimals, SEK when not set as in upsertAccountingRun
    const accountingCurrency = (run && (run.client?.accountingCurrency as CurrencyEnum)) || CurrencyEnum.SEK;
    const accountingDecimals = CURRENCY_EXPONENT[accountingCurrency];

    const { balanceSheetRows, incomeStatementRows } = useMemo(() => {
        const balanceSheetRows: StatementRow[] = [];
        const incomeStatementRows: StatementRow[] = [];
        if (!run || !runsData || !runsData.accountingRuns2 || !journalEntriesData || !journalEntriesData.journalEntries2) {
            return { balanceSheetRows, incomeStatementRows };
        }
        const companyType = (run.client?.accountingCompanyType as AccountingCompanyTypeEnum) || AccountingCompanyTypeEnum.SwedishMutualFund;

        // balances at the run are the journal entries of the runs of the period up to and including it, a later run of the same
        // period adds its transactions and replaces the valuation of the run before it (BVI reverses its BVU)
        const runIds = new Set(
            runsData.accountingRuns2
                .filter((periodRun) => periodRun.accountingPeriod === run.accountingPeriod && periodRun.number <= run.number)
                .map((periodRun) => periodRun._id)
        );
        const unitsByNumber: Record<string, bigint> = {};
        for (const journalEntry of journalEntriesData.journalEntries2) {
            if (!runIds.has(journalEntry.accountingRunId)) continue;
            for (const transaction of journalEntry.transactions || []) {
                if (transaction.amount === null || typeof transaction.amount === "undefined") continue;
                // amounts are exact decimal strings with 4 decimals, summed as bigint in units of 0.0001
                unitsByNumber[transaction.tAccountNumber] =
                    (unitsByNumber[transaction.tAccountNumber] || 0n) + BigInt(transaction.amount.replace(".", ""));
            }
        }

        // a run with the default fund t-account chart has no client chart
        const tAccounts = run.clientTAccountChart ? run.clientTAccountChart.tAccounts || [] : DEFAULT_TACCOUNTS;
        const tAccountsByNumber = Object.fromEntries(tAccounts.map((tAccount) => [tAccount.number, tAccount]));

        const assets = createGroupNode([TAccountGroup1Enum.Assets], companyType);
        const liabilities = createGroupNode([TAccountGroup1Enum.Liabilities], companyType);
        const equity = createGroupNode([TAccountGroup1Enum.Equity], companyType);
        const profitLoss = createGroupNode([TAccountGroup1Enum.Equity, TAccountGroup2Enum.ProfitLossCurrentYear], companyType);
        const ungrouped: GroupNode = { ...createGroupNode(["Ungrouped"], companyType), label: "Ej grupperade konton" };
        for (const tAccountNumber of Object.keys(unitsByNumber)) {
            const units = unitsByNumber[tAccountNumber];
            if (units === 0n) continue;
            const tAccount = tAccountsByNumber[tAccountNumber];
            const path: string[] = [];
            for (const group of [tAccount?.group1, tAccount?.group2, tAccount?.group3, tAccount?.group4, tAccount?.group5]) {
                if (!group || group === "None") break;
                path.push(group);
            }
            const account: AccountBalance = { tAccountNumber, description: tAccount?.description || "", path, units };
            if (path[0] === TAccountGroup1Enum.Equity && path[1] === TAccountGroup2Enum.ProfitLossCurrentYear) {
                addAccount(profitLoss, 2, account, companyType);
            } else if (path[0] === TAccountGroup1Enum.Assets) {
                addAccount(assets, 1, account, companyType);
            } else if (path[0] === TAccountGroup1Enum.Liabilities) {
                addAccount(liabilities, 1, account, companyType);
            } else if (path[0] === TAccountGroup1Enum.Equity) {
                addAccount(equity, 1, account, companyType);
            } else {
                addAccount(ungrouped, 1, { ...account, path: ["Ungrouped"] }, companyType);
            }
        }

        // the result of the year is one line in equity, its accounts are in the income statement
        if (profitLoss.units !== 0n) {
            addAccount(
                equity,
                1,
                { tAccountNumber: "", description: profitLoss.label, path: [TAccountGroup1Enum.Equity], units: profitLoss.units },
                companyType
            );
        }

        for (const group of [assets, liabilities, equity]) {
            if (group.units !== 0n || Object.keys(group.children).length > 0 || group.accounts.length > 0) {
                addGroupRows(group, 0, balanceSheetRows);
            }
        }
        balanceSheetRows.push({
            kind: "sum",
            label: "Summa eget kapital och skulder",
            tAccountNumber: "",
            units: liabilities.units + equity.units,
            level: 0
        });
        if (ungrouped.accounts.length > 0) {
            addGroupRows(ungrouped, 0, balanceSheetRows);
        }
        // booked amounts with debit positive, all journal entries balance so assets, equity, liabilities and ungrouped sum to zero
        balanceSheetRows.push({
            kind: "result",
            label: "Differens",
            tAccountNumber: "",
            units: assets.units + liabilities.units + equity.units + ungrouped.units,
            level: 0
        });

        for (const child of sortBy(Object.values(profitLoss.children), "sorting", "key")) {
            addGroupRows(child, 0, incomeStatementRows);
        }
        for (const account of sortBy(profitLoss.accounts, "tAccountNumber")) {
            incomeStatementRows.push({
                kind: "account",
                label: account.description,
                tAccountNumber: account.tAccountNumber,
                units: account.units,
                level: 0
            });
        }
        incomeStatementRows.push({ kind: "result", label: profitLoss.label, tAccountNumber: "", units: profitLoss.units, level: 0 });

        return { balanceSheetRows, incomeStatementRows };
    }, [run, runsData, journalEntriesData]);

    const columns = useMemo<ReactTableColumnDef<StatementRow>[]>(() => {
        const columnHelper = createReactTableColumnHelper<StatementRow>();

        // the rows are a hierarchy, so no sorting or filtering
        return [
            columnHelper.accessor("label", {
                header: "",
                cell: (cellProps) => {
                    const row = cellProps.row.original;
                    return (
                        <div style={{ paddingLeft: `${row.level}rem`, fontWeight: row.kind === "account" ? "normal" : "bold" }}>
                            {cellProps.getValue()}
                        </div>
                    );
                },
                size: 400,
                enableSorting: false,
                enableColumnFilter: false
            }),
            columnHelper.accessor("tAccountNumber", {
                header: "Account",
                size: 75,
                enableSorting: false,
                enableColumnFilter: false
            }),
            columnHelper.accessor("units", {
                header: "Amount",
                cell: (cellProps) => {
                    const row = cellProps.row.original;
                    // ReactTable replaces null in the first row with "", the first row is a group header without amount
                    if (typeof row.units !== "bigint") return null;
                    // units of 0.0001 back to a decimal string with 4 decimals
                    const digits = (row.units < 0n ? -row.units : row.units).toString().padStart(5, "0");
                    const amount = `${row.units < 0n ? "-" : ""}${digits.slice(0, -4)}.${digits.slice(-4)}`;
                    return (
                        <div style={{ textAlign: "right", fontWeight: row.kind === "account" ? "normal" : "bold" }}>
                            {formatDecimal(amount, accountingDecimals)}
                        </div>
                    );
                },
                size: 150,
                enableSorting: false,
                enableColumnFilter: false
            })
        ];
    }, [accountingDecimals]);

    if (fetching) return <p>Loading</p>;
    if (error) return <p>error: {JSON.stringify(error, null, 2)}</p>;
    if (!run) return <p>Accounting run not found</p>;

    // amounts as numbers so that they can be summed in Excel, labels indented by level
    const exportStatements = () => {
        const balanceSheetSheet = balanceSheetRows.map((row) => ({
            Label: "  ".repeat(row.level) + row.label,
            Account: row.tAccountNumber,
            Amount: row.units === null ? null : Number(row.units) / 10000
        }));
        const incomeStatementSheet = incomeStatementRows.map((row) => ({
            Label: "  ".repeat(row.level) + row.label,
            Account: row.tAccountNumber,
            Amount: row.units === null ? null : Number(row.units) / 10000
        }));
        exportToXlsx(
            [balanceSheetSheet, incomeStatementSheet],
            `${run.client?.name || run.clientId} ${run.accountingPeriod} run ${run.number} statements.xlsx`,
            ["Balance sheet", "Income statement"]
        );
    };

    return (
        <div>
            <Link to={`/accountingruns2/${clientId}/${id}`}>Accounting run {run.number}</Link>
            <h4>Balance sheet and income statement, {run.client?.name}</h4>
            <p>
                Period {run.accountingPeriod}, end date {run.endDate}, accounting run {run.number} ({run.status}), accounting currency{" "}
                {accountingCurrency}. Booked amounts, debit positive and credit negative.
            </p>
            {fetchingRuns || fetchingJournalEntries ? <p>Loading journal entries</p> : null}
            {runsError ? <p>error: {JSON.stringify(runsError, null, 2)}</p> : null}
            {journalEntriesError ? <p>error: {JSON.stringify(journalEntriesError, null, 2)}</p> : null}
            <div className="flex flex-wrap -mx-2">
                <div className="w-full xl:w-1/2 px-2 mb-4">
                    <h5>Balance sheet</h5>
                    <ReactTable columns={columns} data={balanceSheetRows} initialPageSize={1000} onExport={exportStatements} />
                </div>
                <div className="w-full xl:w-1/2 px-2 mb-4">
                    <h5>Income statement</h5>
                    <ReactTable columns={columns} data={incomeStatementRows} initialPageSize={1000} onExport={exportStatements} />
                </div>
            </div>
        </div>
    );
}
